// Le site du club (D-140) : les photos et documents du club. Une photo est réduite à 2000 px sur l'appareil (JPEG) et ne
// part qu'avec la case « J'ai l'accord des personnes visibles » (et des parents pour un mineur) ; 300 photos par club. Un
// PDF : 10 Mo au plus, 50 par club. Les vidéos : un lien YouTube (dans le bloc), jamais un fichier.
import { QUOTA } from '../domain/site';
import { mediaUrl, rememberMedia, siteMediaAdd, siteMediaList, type SiteMedia } from '../lib/site-api';
import { fileToBase64 } from './match-extras';
import { $, $$, html, mount, openSheet, toast } from './dom';
import { ico } from './site-art';

/** Une photo réduite (2000 px au plus, JPEG) : base64, taille en octets, dimensions. */
export async function reducePhoto(file: File, max: number = QUOTA.photoPx, quality = 0.82): Promise<{ data: string; size: number; width: number; height: number }> {
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(bmp.width * k)); c.height = Math.max(1, Math.round(bmp.height * k));
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  let q = quality, data = '';
  for (let i = 0; i < 5; i++) {
    data = c.toDataURL('image/jpeg', q).replace(/^data:[^,]*,/, '');
    if (b64size(data) <= 4_000_000) break;
    q -= 0.12;
  }
  return { data, size: b64size(data), width: c.width, height: c.height };
}
/** Le nombre d'octets d'un base64. */
export const b64size = (d: string) => Math.floor(d.length * 3 / 4) - (d.endsWith('==') ? 2 : d.endsWith('=') ? 1 : 0);

const errText = (e: unknown) => {
  const code = (e as { code?: string }).code, m = String((e as Error).message ?? '');
  if (code === '54000' || /quota/.test(m)) return /document/.test(m) ? 'Le club a déjà 50 documents : retire-en un d’abord.' : 'Le club a déjà 300 photos : retire-en une d’abord.';
  return 'Envoi impossible pour le moment. Réessaie.';
};

/** « Changer la photo » (maquette s5) : prendre une photo, mes photos, les photos du club ; la case d'accord obligatoire
 *  pour une photo nouvelle. Rend l'identifiant de la photo choisie, ou null. */
export function pickPhoto(clubId: string, title = 'Changer la photo', current: string | null = null): Promise<string | null> {
  return new Promise(resolve => {
    let chosen: string | null = current, fresh: { file: File; url: string } | null = null, done = false;
    const finish = (v: string | null) => { if (!done) { done = true; resolve(v); } };
    openSheet(title, html`
      <div class="form" id="sm-photo">
        <div class="se-src">
          <label data-guide="site-photo-take">${ico('camera')}Prendre une photo<input type="file" accept="image/*" capture="environment" hidden data-file></label>
          <label>${ico('image')}Mes photos<input type="file" accept="image/jpeg,image/png,image/webp,image/heic" hidden data-file></label>
          <button type="button" data-lib aria-pressed="false">${ico('gallery')}Photos du club</button>
        </div>
        <div class="se-lib" id="sm-lib" hidden></div>
        <div class="se-photo" id="sm-prev">${chosen ? html`<img alt="">` : html`<span>Choisis une photo</span>`}</div>
        <label class="check" id="sm-consent-row" data-guide="site-photo-consent"><input type="checkbox" id="sm-consent" ${current ? 'checked disabled' : ''}>
          <span><strong>J’ai l’accord des personnes visibles</strong><br><span class="muted">Et celui des parents pour un mineur. Sans cette case, la photo ne part pas.</span></span></label>
        <p class="form-msg" role="alert"></p>
        <button type="button" class="btn btn-primary" id="sm-use" disabled>Utiliser cette photo</button>
        <p class="hint">Réduite à 2 000 px sur ton téléphone avant l’envoi. Jamais de photo de joueur ni de joueuse dans les blocs vivants : ils se remplissent tout seuls, sans nom ni photo.</p>
      </div>`, (sheet, close) => {
      const prev = $(sheet, '#sm-prev')!, use = $<HTMLButtonElement>(sheet, '#sm-use')!, consent = $<HTMLInputElement>(sheet, '#sm-consent')!;
      const msg = (t: string) => { $(sheet, '.form-msg')!.textContent = t; };
      const show = (url: string | null) => { mount(prev, url ? html`<img src="${url}" alt="">` : html`<span>Choisis une photo</span>`); };
      const ready = () => { use.disabled = !(fresh ? consent.checked : chosen && chosen !== current); };
      if (current) void mediaUrl(current, { club: clubId }).then(show);
      for (const inp of $$<HTMLInputElement>(sheet, '[data-file]')) inp.addEventListener('change', () => {
        const f = inp.files?.[0];
        if (!f) return;
        if (fresh) URL.revokeObjectURL(fresh.url);
        fresh = { file: f, url: URL.createObjectURL(f) };
        consent.disabled = false; consent.checked = false;
        show(fresh.url); msg(''); ready();
      });
      consent.addEventListener('change', ready);
      $(sheet, '[data-lib]')!.addEventListener('click', async (e) => {
        const b = e.currentTarget as HTMLButtonElement, lib = $(sheet, '#sm-lib')!;
        lib.hidden = !lib.hidden; b.setAttribute('aria-pressed', String(!lib.hidden));
        if (lib.hidden || lib.dataset.loaded) return;
        lib.dataset.loaded = '1';
        const list = (await siteMediaList(clubId).catch(() => [] as SiteMedia[])).filter(m => m.kind === 'photo');
        mount(lib, list.length ? html`${list.map(m => html`<button type="button" data-id="${m.id}" aria-label="${m.name}"><img alt=""></button>`)}`
          : html`<p class="muted small">Pas encore de photo du club.</p>`);
        for (const btn of $$<HTMLButtonElement>(lib, '[data-id]')) {
          void mediaUrl(btn.dataset.id!, { club: clubId }).then(u => { if (u) btn.querySelector('img')!.src = u; });
          btn.addEventListener('click', () => {
            chosen = btn.dataset.id!; fresh = null;
            $$(lib, '[data-id]').forEach(x => x.setAttribute('aria-pressed', String(x === btn)));
            consent.checked = true; consent.disabled = true;
            void mediaUrl(chosen, { club: clubId }).then(show); ready();
          });
        }
      });
      use.addEventListener('click', async () => {
        if (!fresh) { finish(chosen); close(); return; }
        if (!consent.checked) { msg('Coche la case : la photo ne part pas sans l’accord des personnes visibles.'); return; }
        use.disabled = true; msg('Envoi…');
        try {
          const p = await reducePhoto(fresh.file);
          const id = await siteMediaAdd(clubId, { kind: 'photo', name: fresh.file.name || 'photo.jpg', mime: 'image/jpeg', size: p.size, width: p.width, height: p.height, data: p.data, consent: true });
          rememberMedia(id, clubId, `data:image/jpeg;base64,${p.data}`);
          finish(id); close();
        } catch (e) { use.disabled = false; msg(errText(e)); }
      });
    }, () => finish(null));
  });
}

/** Un document PDF du club (10 Mo au plus) : rend son identifiant et son nom, ou null. */
export function pickPdf(clubId: string): Promise<{ id: string; name: string } | null> {
  return new Promise(resolve => {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.accept = 'application/pdf';
    inp.addEventListener('change', async () => {
      const f = inp.files?.[0];
      if (!f) { resolve(null); return; }
      if (f.size > QUOTA.pdfBytes) { toast('Un PDF de 10 Mo au plus.', 'ko'); resolve(null); return; }
      try {
        const data = await fileToBase64(f);
        if (!data.startsWith('JVBERi0')) { toast('Ce fichier n’est pas un PDF.', 'ko'); resolve(null); return; }
        const id = await siteMediaAdd(clubId, { kind: 'pdf', name: f.name || 'document.pdf', mime: 'application/pdf', size: b64size(data), data, consent: false });
        resolve({ id, name: f.name.replace(/\.pdf$/i, '') });
      } catch (e) { toast(errText(e), 'ko'); resolve(null); }
    });
    inp.click();
  });
}
